import { useChannelLabel } from "../../features/ai/agents/agent-labels";
import { PageHeader } from "../../components/PageHeader";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { buttonVariantClasses, cx, Icon, IconButton, type IconName } from "@wato/ui";
import { useI18n } from "@wato/i18n/react";
import { isoDayInZone } from "../../lib/analytics-range";
import { useApi, useErrorMessage } from "../../api/context";
import type { AnalyticsOverviewView } from "../../api/analytics-types";
import type { ChannelSummary } from "../../api/types";
import { PageError } from "../../components/common";
import { ChartCard, EmptyData, KpiCard, KpiSkeleton } from "../../components/analytics/cards";
import { formatAmount } from "../../features/billing/format";
import { useWorkspace } from "../../features/workspace/WorkspaceContext";
import { CsatCard } from "../../features/analytics/csat/CsatCard";
import { downloadCsv } from "../../lib/download";
import { toCsv } from "../../lib/analytics-range";
import type { AnalyticsConversationsView } from "../../api/analytics-types";
import { DeveloperIntro } from "../../features/settings/DeveloperIntro";

type State =
  | { status: "loading" }
  | { status: "ready"; overview: AnalyticsOverviewView }
  | { status: "error"; error: unknown };

const DAY_MS = 86_400_000;
const isoDay = (d: Date) => isoDayInZone(d);

/** `4200` bp -> "42%"; null -> dash. Rates are integer basis points, never floats on the wire. */
export function formatBasisPoints(bp: number | null): string {
  return bp === null ? "—" : `${(bp / 100).toFixed(bp % 100 === 0 ? 0 : 1)}%`;
}

/** WP-420 Analytics Overview: date range, per-channel filter, KPI tiles, billing-currency money. */
export function AnalyticsOverviewPage() {
  const { t, locale } = useI18n();
  const api = useApi();
  const errorMessage = useErrorMessage();
  const { workspace } = useWorkspace();
  const today = useMemo(() => new Date(), []);
  const [from, setFrom] = useState(isoDay(new Date(today.getTime() - 29 * DAY_MS)));
  const [to, setTo] = useState(isoDay(today));
  const [channelId, setChannelId] = useState("");
  const channelLabel = useChannelLabel();
  const [channels, setChannels] = useState<ChannelSummary[]>([]);
  const [state, setState] = useState<State>({ status: "loading" });
  const [byChannel, setByChannel] = useState<AnalyticsConversationsView | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    api.listChannels(workspace.id).then(
      (list) => live && setChannels(list),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [api, workspace.id]);

  const rangeValid = from !== "" && to !== "" && from <= to;
  useEffect(() => {
    if (!rangeValid) return;
    let live = true;
    setState({ status: "loading" });
    api.getAnalyticsOverview(workspace.id, { from, to, ...(channelId ? { channelId } : {}) }).then(
      (overview) => live && setState({ status: "ready", overview }),
      (error: unknown) => live && setState({ status: "error", error }),
    );
    // Per-channel breakdown is secondary: a failure just hides the table.
    api.getAnalyticsConversations(workspace.id, { from, to }).then(
      (v) => live && setByChannel(v),
      () => live && setByChannel(null),
    );
    return () => {
      live = false;
    };
  }, [api, workspace.id, from, to, channelId, rangeValid, attempt]);

  const num = new Intl.NumberFormat(locale === "ar" ? "ar-u-nu-latn" : "en");

  return (
    <section aria-label={t("analytics.overview.title")} className="flex flex-col gap-5">
      <PageHeader
        title={t("analytics.overview.title")}
        helpContent={t("analytics.overview.subtitle")}
        help={<DeveloperIntro page="overview" />}
        secondary={
          <Link
            to={`/w/${workspace.id}/reports/weekly`}
            className="text-sm text-on-action-ghost hover:underline"
            data-testid="weekly-report-link"
          >
            {t("command.action.weeklyReport")}
          </Link>
        }
      />
      <form
        className="flex flex-wrap items-end gap-4 rounded-2xl border border-line bg-surface p-5"
        onSubmit={(e) => e.preventDefault()}
      >
        <label className="flex flex-col gap-1 text-sm text-fg-muted">
          {t("analytics.filters.from")}
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="h-11 rounded-xl border border-line bg-surface-sunken px-3 text-fg"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-fg-muted">
          {t("analytics.filters.to")}
          <input
            type="date"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
            className="h-11 rounded-xl border border-line bg-surface-sunken px-3 text-fg"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-fg-muted">
          {t("analytics.filters.channel")}
          <select
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            className="h-11 rounded-xl border border-line bg-surface-sunken px-3 text-fg"
          >
            <option value="">{t("analytics.filters.allChannels")}</option>
            {channels.map((c) => (
              <option key={c.id} value={c.id}>
                {channelLabel(c)}
              </option>
            ))}
          </select>
        </label>
      </form>
      {!rangeValid ? (
        <p role="alert" className="text-sm text-danger-fg">
          {t("analytics.filters.invalidRange")}
        </p>
      ) : state.status === "loading" ? (
        <KpiSkeleton />
      ) : state.status === "error" ? (
        <PageError message={errorMessage(state.error)} onRetry={() => setAttempt((n) => n + 1)} />
      ) : (
        <Ready
          overview={state.overview}
          byChannel={byChannel}
          channels={channels}
          workspaceId={workspace.id}
          format={(n) => num.format(n)}
          locale={locale}
        />
      )}
      {rangeValid && <CsatCard from={from} to={to} />}
    </section>
  );
}

const KPI_ICONS: Readonly<Record<string, IconName>> = {
  conversationsNew: "inbox",
  conversationsOpen: "message",
  conversationsClosed: "check",
  messagesSent: "send",
  messagesReceived: "reply",
  activeContacts: "users",
  automationRate: "sparkles",
};

const MIN_MS = 60_000;

/** Whole minutes for short spans, hours (1 dp) beyond that; null -> dash. */
function formatDuration(
  ms: number | null,
  num: Intl.NumberFormat,
  t: ReturnType<typeof useI18n>["t"],
): string {
  if (ms === null) return "—";
  const min = Math.round(ms / MIN_MS);
  return min < 120
    ? t("analytics.channels.minutes", { value: num.format(min) })
    : t("analytics.channels.hours", { value: num.format(Math.round(min / 6) / 10) });
}

function Ready({
  overview,
  byChannel,
  channels,
  workspaceId,
  format,
  locale,
}: {
  overview: AnalyticsOverviewView;
  byChannel: AnalyticsConversationsView | null;
  channels: ChannelSummary[];
  workspaceId: string;
  format: (n: number) => string;
  locale: "ar" | "en";
}) {
  const { t } = useI18n();
  const channelLabel = useChannelLabel();
  const numFmt = new Intl.NumberFormat(locale === "ar" ? "ar-u-nu-latn" : "en");
  // Deleted channels are not listed (their conversations stay in the totals).
  const byChannelRows = (byChannel?.byChannel ?? []).flatMap((row) => {
    const channel = channels.find((x) => x.id === row.channelId);
    return channel ? [{ row, channel }] : [];
  });
  const empty =
    overview.conversations.new + overview.conversations.open + overview.conversations.closed ===
      0 &&
    overview.messages.sent + overview.messages.received === 0 &&
    overview.daily.length === 0;
  const exportCsv = () => {
    const header = ["day", "conversations_new", "messages_sent", "messages_received"];
    const rows = overview.daily.map((d) => [
      d.day,
      d.conversationsNew,
      d.messagesSent,
      d.messagesReceived,
    ]);
    downloadCsv({
      filename: `analytics-overview_${overview.from}_${overview.to}.csv`,
      csv: toCsv(header, rows),
    });
  };
  const days = Math.max(1, overview.daily.length);
  const perDay = (n: number): string | undefined =>
    overview.daily.length > 0 && n > 0
      ? t("analytics.kpi.perDay", { value: format(Math.round((n / days) * 10) / 10) })
      : undefined;
  const tiles: {
    key:
      | "conversationsNew"
      | "conversationsOpen"
      | "conversationsClosed"
      | "messagesSent"
      | "messagesReceived"
      | "activeContacts"
      | "automationRate";
    value: string;
    hint?: string | undefined;
  }[] = [
    {
      key: "conversationsNew",
      value: format(overview.conversations.new),
      hint: perDay(overview.conversations.new),
    },
    { key: "conversationsOpen", value: format(overview.conversations.open) },
    { key: "conversationsClosed", value: format(overview.conversations.closed) },
    {
      key: "messagesSent",
      value: format(overview.messages.sent),
      hint: perDay(overview.messages.sent),
    },
    {
      key: "messagesReceived",
      value: format(overview.messages.received),
      hint: perDay(overview.messages.received),
    },
    { key: "activeContacts", value: format(overview.activeContactDays) },
    { key: "automationRate", value: formatBasisPoints(overview.automation.rateBasisPoints) },
  ];
  if (empty) {
    return (
      <div data-testid="analytics-empty-cta" className="flex flex-col items-center gap-3">
        <EmptyData />
        <Link
          to={`/w/${workspaceId}/${channels.length === 0 ? "channels" : "inbox"}`}
          className={cx(
            "inline-flex h-11 items-center rounded-xl px-5 text-sm font-semibold",
            buttonVariantClasses.primary,
          )}
        >
          {channels.length === 0 ? t("analytics.empty.ctaConnect") : t("analytics.empty.ctaInbox")}
        </Link>
      </div>
    );
  }
  return (
    <>
      <dl className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="kpi-tiles">
        {tiles.map((tile) => (
          <KpiCard
            key={tile.key}
            icon={KPI_ICONS[tile.key] ?? "chart"}
            label={t(`analytics.kpi.${tile.key}`)}
            value={tile.value}
            hint={tile.hint}
            valueTestId={`kpi-${tile.key}`}
            title={t(`analytics.kpiHelp.${tile.key}`)}
          />
        ))}
        {overview.money.length === 0 ? (
          <KpiCard icon="wallet" label={t("analytics.kpi.revenue")} value="—" />
        ) : null}
        {overview.money.map((m) => (
          <div key={m.currency} className="contents" data-testid={`money-${m.currency}`}>
            {(["aiCost", "metaCost", "revenue"] as const).map((k) => {
              const amount =
                k === "aiCost"
                  ? m.aiCostMinor
                  : k === "metaCost"
                    ? m.metaCostEstimateMinor
                    : m.revenueMinor;
              const basis =
                k === "revenue"
                  ? undefined
                  : t("analytics.kpi.costBasis", {
                      ai: formatAmount(m.aiCostMinor, m.currency, locale),
                      meta: formatAmount(m.metaCostEstimateMinor, m.currency, locale),
                    });
              return (
                <KpiCard
                  key={k}
                  icon="wallet"
                  label={t(`analytics.kpi.${k}`)}
                  value={formatAmount(amount, m.currency, locale)}
                  valueTestId={`kpi-${k}-${m.currency}`}
                  title={basis}
                />
              );
            })}
          </div>
        ))}
      </dl>
      {overview.money.length > 1 ? (
        <p className="text-sm text-fg-muted">{t("analytics.kpi.mixedCurrency")}</p>
      ) : null}
      {byChannelRows.length > 0 ? (
        <ChartCard title={t("analytics.channels.title")}>
          <div className="overflow-x-auto">
            <table
              className="w-full text-start text-sm tabular-nums"
              data-testid="channel-breakdown"
            >
              <thead>
                <tr className="text-xs text-fg-muted">
                  <th className="pb-2 text-start font-medium">{t("analytics.filters.channel")}</th>
                  <th className="pb-2 text-start font-medium">
                    {t("analytics.kpi.conversationsNew")}
                  </th>
                  <th
                    className="pb-2 text-start font-medium"
                    title={t("analytics.kpiHelp.firstResponse")}
                  >
                    {t("analytics.channels.firstResponse")}
                  </th>
                  <th
                    className="pb-2 text-start font-medium"
                    title={t("analytics.kpiHelp.resolution")}
                  >
                    {t("analytics.channels.resolution")}
                  </th>
                  <th className="pb-2 text-start font-medium" title={t("analytics.kpiHelp.reopen")}>
                    {t("analytics.channels.reopen")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {byChannelRows.map(({ row: c, channel: ch }) => {
                  return (
                    <tr
                      key={c.channelId}
                      data-testid="channel-row"
                      className="border-t border-line"
                    >
                      <td className="py-3">{channelLabel(ch)}</td>
                      <td className="py-3">{format(c.conversationsNew)}</td>
                      <td className="py-3">{formatDuration(c.avgFirstResponseMs, numFmt, t)}</td>
                      <td className="py-3">{formatDuration(c.avgResolutionMs, numFmt, t)}</td>
                      <td className="py-3">{formatBasisPoints(c.reopenRateBasisPoints)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </ChartCard>
      ) : null}
      <ChartCard title={t("analytics.daily.caption")}>
        {overview.daily.length > 0 ? (
          <IconButton
            label={t("analytics.export.csv")}
            icon={<Icon name="download" />}
            data-testid="export-csv"
            onClick={exportCsv}
            className="self-start"
          />
        ) : null}
        {overview.daily.length === 0 ? (
          <EmptyData />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm tabular-nums">
              <thead>
                <tr className="text-xs text-fg-muted">
                  <th className="pb-2 text-start font-medium">{t("analytics.daily.day")}</th>
                  <th className="pb-2 text-start font-medium">
                    {t("analytics.kpi.conversationsNew")}
                  </th>
                  <th className="pb-2 text-start font-medium">{t("analytics.kpi.messagesSent")}</th>
                  <th className="pb-2 text-start font-medium">
                    {t("analytics.kpi.messagesReceived")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {overview.daily.map((d) => (
                  <tr key={d.day} data-testid="daily-row" className="border-t border-line">
                    <td className="py-3">{d.day}</td>
                    <td className="py-3">{format(d.conversationsNew)}</td>
                    <td className="py-3">{format(d.messagesSent)}</td>
                    <td className="py-3">{format(d.messagesReceived)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ChartCard>
    </>
  );
}
